import { call, put, select } from 'redux-saga/effects';
import { Route, State as RouteState, DecodedRouteParams } from 'config/routes';
import { authGate, AuthGateResponse, waitForUserResolve } from '../utils';
import { AuthenticationModalType } from 'components/authentication/types';
import { isVisitorUserSelector, approvedAgentSelector, isAgentManagerSelector } from 'store/state/domainData/selectors';
import { navigateTo } from 'store/state/router/actions';
import { getRouteParams, prevRouteSelector } from 'store/state/selectors/router';
import { loadListingsWorker } from 'store/sagas/routing/handlers/manageBulletinsPage';
import { UploadBulletinStep } from 'components/bulletin-form/types';


const LATE_AUTH_REQUIRED_STEPS = new Set([
  UploadBulletinStep.UploadImage,
  UploadBulletinStep.Contacts,
  UploadBulletinStep.Preview,
]);


export function* uploadBulletinWizardHandler(route: RouteState) {
  if (route.params.step === UploadBulletinStep.Preview) {
    yield call(maybeNavigateFromDirectPreviewToFirstStep, route.name);
  }

  yield call(waitForUserResolve);
  const isVisitor: boolean = yield select(isVisitorUserSelector);
  const isApprovedAgent: boolean = yield select(approvedAgentSelector);
  const isAgentManager: boolean = yield select(isAgentManagerSelector);
  const routeParams: DecodedRouteParams = yield select(getRouteParams);
  const previousRoute = yield select(prevRouteSelector);

  // maybe replace externalUploadSource with check exactly with AI prev route route comparing condition
  if (routeParams.externalUploadSource || routeParams.referrer) {
    if (isApprovedAgent || isAgentManager) {
      yield put(navigateTo(Route.UploadBulletinForm, routeParams, { replace: true }));
    }
    else if (!isVisitor && !previousRoute) {
      yield put(navigateTo(Route.UploadBulletin, { ...routeParams, step: UploadBulletinStep.MainInfo }, { replace: false }));
    }

  }

  if (route.params.step && isVisitor) {
    const shouldRequireAuth = LATE_AUTH_REQUIRED_STEPS.has(route.params.step);

    if (shouldRequireAuth) {
      const status: AuthGateResponse = yield call(authGate, undefined, AuthenticationModalType.SignIn);

      if (status === AuthGateResponse.Rejected) {
        yield put(navigateTo(Route.Home, {}, { replace: true }));
      }
    }
  }
  else {
    yield call(maybeLoadListings, route.name);
    yield call(maybeNavigateToFirstStep, route.name);
  }
}

function* maybeNavigateFromDirectPreviewToFirstStep(bulletinWizardRoute: Route) {
  const previousRoute = yield select(prevRouteSelector);

  if (!previousRoute) {
    yield put(navigateTo(bulletinWizardRoute, { step: undefined }, { replace: true }));
  }
}

function* maybeNavigateToFirstStep(bulletinWizardRoute: Route) {
  const { step, ...restParams } = yield select(getRouteParams);
  const previousRoute = yield select(prevRouteSelector);

  if (step && (!previousRoute || previousRoute && previousRoute.name !== bulletinWizardRoute)) {
    yield put(navigateTo(bulletinWizardRoute, { ...restParams, step: undefined }, { replace: true }));
  }
}

function* maybeLoadListings(bulletinWizardRoute: Route) {
  const previousRoute = yield select(prevRouteSelector);

  if ((!previousRoute || previousRoute && previousRoute.name !== bulletinWizardRoute)) {
    yield call(loadListingsWorker);
  }
}
